import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import DebugToolWidget from '../../../components/DebugToolWidget/DebugToolWidget';
import { getPlanByApiAndOrganization } from '../../../redux/actions/plans';
import { getApiByFriendlyName } from '../../../redux/actions/apis';
import { TrackPostHogEvent } from '../../../shared/utils/posthog';

const FirstRequest = ({ 
  onFirstRequestComplete, 
  onNext, 
  hasCompletedFirstRequest,
  debuggerForm,
  user,
  plans,
  organization,
  apis,
  getPlanByApiAndOrganization,
  getApiByFriendlyName
}) => {
  const [isSettingUpApi, setIsSettingUpApi] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const [requestSucceeded, setRequestSucceeded] = useState(false);
  const [apiAttemptCount, setApiAttemptCount] = useState(0);

  // Track when first request step is entered
  useEffect(() => {
    TrackPostHogEvent('onboarding_first_request_step_entered', {
      has_api_key: plans && plans.detailPlan && plans.detailPlan.apiKeys && plans.detailPlan.apiKeys.length > 0,
      current_request_count: organization && organization.currentOrg && organization.currentOrg.currentBillingCycle && organization.currentOrg.currentBillingCycle.totalCalls || 0,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Watch for debugger form loading state changes
  useEffect(() => {
    if (debuggerForm && debuggerForm.loading) {
      const newAttemptCount = apiAttemptCount + 1;
      setApiAttemptCount(newAttemptCount);
      
      // Track API request attempt
      TrackPostHogEvent('onboarding_api_request_attempted', {
        user_id: user && user.id,
        attempt_number: newAttemptCount,
        url_used: debuggerForm && debuggerForm.defaultOptions && debuggerForm.defaultOptions.url || 'Unknown',
        timestamp: new Date().toISOString()
      });
    }
  }, [debuggerForm && debuggerForm.loading]);

  // Load required data and ensure API key exists
  useEffect(() => {
    const setupApiAccess = () => {
      if (!user || !organization.currentOrg) {
        return; // Wait for user and organization to be loaded
      }

      // Step 1: Ensure API is loaded
      if (!apis.detailApi.id) {
        getApiByFriendlyName('Opengraphio');
        return; // Wait for API to load before proceeding
      }

      // Step 2: Ensure plan is loaded
      if (!plans.detailPlan.id) {
        getPlanByApiAndOrganization();
        return; // Wait for plan to load before proceeding
      }

    };

    setupApiAccess();
  }, [
    user, 
    organization.currentOrg, 
    apis.detailApi.id, 
    plans.detailPlan.id, 
    plans.detailPlan.apiKeys,
    getPlanByApiAndOrganization,
    getApiByFriendlyName
  ]);


  // Watch for successful API calls and check if requests were actually consumed
  useEffect(() => {
    if (debuggerForm && debuggerForm.site && debuggerForm.site.results) {
      const results = debuggerForm.site.results;
      
      // Check if the API call consumed requests
      const requestCount = results.requestInfo && results.requestInfo.requestCount;
      
      if (requestCount && requestCount > 0) {
        // Successfully consumed requests - complete the first request
        setRequestSucceeded(true);
        setRequestFailed(false);
        
        TrackPostHogEvent('onboarding_api_request_successful', {
          request_count_consumed: requestCount,
          attempt_number: apiAttemptCount,
          url_used: debuggerForm && debuggerForm.defaultOptions && debuggerForm.defaultOptions.url || 'Unknown',
          response_time: results.requestInfo && results.requestInfo.responseTime || null,
          timestamp: new Date().toISOString()
        });
        
        onFirstRequestComplete();
      } else {
        // API call succeeded but no requests were consumed - show retry message
        setRequestFailed(true);
        setRequestSucceeded(false);
      }
    }
  }, [debuggerForm && debuggerForm.site && debuggerForm.site.results, hasCompletedFirstRequest, onFirstRequestComplete, apiAttemptCount]);

  // Watch for API call errors
  useEffect(() => {
    if (debuggerForm && debuggerForm.alert) {
      setRequestFailed(true);
      setRequestSucceeded(false);
      
      // Track API request error
      TrackPostHogEvent('onboarding_api_request_failed', {
        attempt_number: apiAttemptCount,
        error_message: debuggerForm.alert,
        url_used: debuggerForm && debuggerForm.defaultOptions && debuggerForm.defaultOptions.url || 'Unknown',
        timestamp: new Date().toISOString()
      });
    }
  }, [debuggerForm && debuggerForm.alert, apiAttemptCount]);

  // Track continue to plans button click
  const handleContinueClick = () => {
    TrackPostHogEvent('onboarding_continue_to_plans_clicked', {      
      successful_requests_made: requestSucceeded,
      total_attempts: apiAttemptCount,
      final_request_count: organization && organization.currentOrg && organization.currentOrg.currentBillingCycle && organization.currentOrg.currentBillingCycle.totalCalls || 0,
      timestamp: new Date().toISOString()
    });
    
    onNext();
  };

  // Check if user is authenticated and has necessary data
  const isUserReady = user && organization.currentOrg;
  const isApiReady = isUserReady && apis.detailApi && apis.detailApi.id;
  const isPlanReady = isApiReady && plans.detailPlan && plans.detailPlan.id;
  const hasApiKey = isPlanReady && plans.detailPlan.apiKeys && plans.detailPlan.apiKeys.length > 0;
  const canMakeRequests = hasApiKey && !isSettingUpApi;

  if (!isUserReady) {
    return (
      <div className="first-request">
        <div className="first-request-header">
          <h2>Setting up your account...</h2>
          <p className="first-request-subtext">
            Please wait while we prepare your API access.
          </p>
        </div>
      </div>
    );
  }

  if (!isApiReady) {
    return (
      <div className="first-request">
        <div className="first-request-header">
          <h2>Loading API...</h2>
          <p className="first-request-subtext">
            Connecting to OpenGraph.io API.
          </p>
        </div>
      </div>
    );
  }

  if (!isPlanReady) {
    return (
      <div className="first-request">
        <div className="first-request-header">
          <h2>Setting up your plan...</h2>
          <p className="first-request-subtext">
            Configuring your API access plan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="first-request">
      <div className="first-request-header">
        <h2>Try it out — make your first request</h2>
        <p className="first-request-subtext">
          Click on a test URL below to instantly see what OpenGraph.io returns. No code required.
        </p>
      </div>

      <div className="first-request-content">
        <div className="url-section">
          <p className="url-section-cta">Pick a URL to fetch metadata, or enter a custom URL below →</p>
          <DebugToolWidget 
            buttonTitle="Submit"
            tab="onboarding"
            disabled={!canMakeRequests}
          />
        </div>

        {(hasCompletedFirstRequest && requestSucceeded) && (
          <div className="next-button-container">
            <Button 
              color="success" 
              size="lg" 
              onClick={handleContinueClick}
              className="continue-button"
            >
              Continue to Plans →
            </Button>
          </div>
        )}

        {requestFailed && !requestSucceeded && (
          <div className="request-failed-message">
            <div className="alert alert-warning" style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              color: '#856404'
            }}>
              <h5>Request didn't count — try again!</h5>
              <p style={{ margin: 0 }}>
                Please try a different URL or try again to complete your first billable request.
              </p>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  debuggerForm: state.debuggerForm,
  user: state.users.user,
  plans: state.plans,
  organization: state.organization,
  apis: state.apis,
});

const mapDispatchToProps = {
  getPlanByApiAndOrganization,
  getApiByFriendlyName,
};

export default connect(mapStateToProps, mapDispatchToProps)(FirstRequest); 